import React, { useMemo, Fragment } from "react";
import { components } from 'common';
import { lang } from '@umu/umu-util';
import { classSet } from '@umu/umu-dom';
import { CERTIFICATE_IMG_TYPE} from '../../interface/index';
import { QrCodeContent } from '../../components/index';
import { QR_CODE_WIDTH, getElementExhibitConf, convertImage2PngFormat } from '../../dataflow/index';
import { ICertificateThemeProps } from '../../interface/common';


const { UMUBg, If } = components;

const EnterpriseTheme1 = (props: ICertificateThemeProps) => {
    const { themeInfo } = props;
    const {
        certificateName,
        certificateText,
        enterpriseInfo,
        certificateDisplayConfig: conf,
        traineesName,
        traineesSubName,
        courseInfo = {
            courseName: ''
        },
    } = themeInfo;

    const getEnterpriseLogo = () => {
        return enterpriseInfo.logoType === CERTIFICATE_IMG_TYPE.IMG_TYPE ? (
            <UMUBg clsName="certificate-enterprise-logo" bgSrc={convertImage2PngFormat(enterpriseInfo.logo)} />
        ) : (
            enterpriseInfo.logo
        );
    };
    const logoContent = useMemo(() => {
        return !!enterpriseInfo.logo ? getEnterpriseLogo() : <div className="empty-logo" />;
    }, [enterpriseInfo.logo, enterpriseInfo.logoType]);

    const certificateNameContent = useMemo(() => {
        const isHasCertificateName = !!certificateName;
        return (
            <div
                className={classSet({
                    'certificate-title text-row-more text-row-2': isHasCertificateName,
                    'empty-certificate-name': !isHasCertificateName,
                })}
            >
                {certificateName || ''}
            </div>
        );
    }, [certificateName]);

    const traineesNameContent = useMemo(() => {
        const isShow = !!traineesName || !!traineesSubName;
        return (
            <div className={`${isShow ? '' : 'empty-'}trainees-name content-trainees-name-split`}>
                <If condition={!!traineesName}>
                    <div className="text-row-1 title-name">{traineesName}</div>
                </If>
                <If condition={!!traineesSubName}>
                    <div className="text-row-more text-row-2 trainees-sub-name">{traineesSubName}</div>
                </If>
            </div>
        );
    }, [traineesName, traineesSubName]);

    const renderBasicInfo = () => {
        const isHasCourseName = courseInfo && courseInfo.courseName;
        const text = isHasCourseName
            ? lang('《{$0}》').replace('{$0}', courseInfo.courseName)
            : lang('未关联课程');

        return (
            <Fragment>
                {logoContent}
                {certificateNameContent}
                <div className="certificate-main-content">
                    {traineesNameContent}
                    <div className="certificate-text text-row-more">{certificateText}</div>
                    <If condition={!!conf.isShowCourseName || !!conf.isShowSourceName}>
                        <div className="course-name content-course-name-split text-row-more text-row-2">{text}</div>
                    </If>
                </div>
            </Fragment>
        );
    };

    const renderCertificateIssueFooter = () => {
        const isShowQr = !!conf.isShowCertificateQRCode;
        const isShowIssueTime = !!conf.isShowCertificateIssueTime;
        const isShowUnit = !!enterpriseInfo.issuingUnitName;
        return (
            <div
                className={classSet({
                    'certificate-issue-footer': true,
                    'hide-qr': !isShowQr,
                })}
            >
                <div
                    className={classSet({
                        'custom-label issue-footer-col': true,
                        'certificate-issue-time': isShowQr,
                        'certificate-issue-time-unit': !isShowQr && isShowUnit,
                        'certificate-issue-time-only': !isShowQr && !isShowUnit,
                        'certificate-issue-time-display': !isShowIssueTime && !isShowQr,
                    })}
                >
                    <If condition={isShowIssueTime}>
                        <Fragment>
                            <div className="certificate-issue-title">
                                {conf.setUpInfo ? conf.setUpInfo.certificateIssueTime : ''}
                            </div>
                            <div className="certificate-issue-sub">{lang('颁发日期')}</div>
                        </Fragment>
                    </If>
                </div>
                <div
                    className={classSet({
                        'certificate-issue-qr issue-footer-col': true,
                        'hide': !isShowQr && !isShowIssueTime,
                    })}
                >
                    <If condition={isShowQr}>
                        <QrCodeContent
                            id="certificateId"
                            codeOptions={{
                                text: conf.setUpInfo ? conf.setUpInfo.QRCodeUrl : 'https://umu.cn',
                                width: QR_CODE_WIDTH,
                                height: QR_CODE_WIDTH,
                            }}
                        />
                    </If>
                </div>
                <If condition={isShowUnit}>
                    <div
                        className={classSet({
                            'custom-label issue-footer-col': true,
                            'certificate-issue-time': isShowQr,
                            'certificate-issue-time-unit': !isShowQr && isShowIssueTime,
                            'certificate-issue-time-only': !isShowQr && !isShowIssueTime,
                        })}
                    >
                        <div className="certificate-issue-title">{enterpriseInfo.issuingUnitName}</div>
                        <div className="certificate-issue-sub">{lang('颁发单位')}</div>
                    </div>
                </If>
            </div>
        );
    };

    const renderCertificateIssueInfo = () => {
        const exhibitConf = getElementExhibitConf(conf);

        const validPeriodTimeContent = useMemo(() => {
            const validPeriodTimeRangeContent = (
                <Fragment>
                    <If condition={exhibitConf.isEffective}>
                        <span>{!!exhibitConf.isEndTime ? exhibitConf.certificateEffective : lang('${0} 起生效').replace('${0}', exhibitConf.certificateEffective)}</span>
                    </If>
                    <If condition={exhibitConf.isShowSplit}>
                        <span className="date-split-line">-</span>
                    </If>
                    <If condition={exhibitConf.isEndTime}>
                        <span>{!exhibitConf.isEffective && !exhibitConf.certificateIsPermanent ? lang('至 ${0}').replace('${0}', exhibitConf.certificateEndTime) : exhibitConf.certificateEndTime}</span>
                    </If>
                </Fragment>
            );

            return exhibitConf.isShowValidPeriod ? (
                <div className="certificate-issue-validity-time">
                    <p className="certificate-issue-title">
                        {exhibitConf.validPeriodTime ? (
                            <span>{exhibitConf.validPeriodTime}</span>
                        ) : (
                            validPeriodTimeRangeContent
                        )}
                    </p>
                    <p className="certificate-issue-sub">{lang('有效期限')}</p>
                </div>
            ) : null;
        }, [exhibitConf]);

        return (
            <div className="certificate-issue-info">
                {validPeriodTimeContent}
                {renderCertificateIssueFooter()}
            </div>
        );
    };

    return (
        <div className="certificate-view-info certificate-basic-info">
            {renderBasicInfo()}
            {renderCertificateIssueInfo()}
        </div>
    );

}

export {
    EnterpriseTheme1
}
